// Playback-side data helpers. Both features/events and features/playback are
// owned by the same feature area (F2, spec 17); the meta-aware fetch + event
// types live in ../events/eventsLib and are re-exported here so playback code
// has a single local import surface.

import { apiFetch } from '../../lib/api';
import { fetchDeviceEvents, type EventItem } from '../events/eventsLib';

export { dayWindowUs, toDateInputValue, fetchDeviceEvents } from '../events/eventsLib';
export type { EventItem } from '../events/eventsLib';
// WEB3-F2: DeviceSummary 的單一權威是 features/devices/deviceModel（eventsLib 的
// 本地閹割版已刪——無 display_name、online 誤型別 boolean）。
export type { DeviceSummary } from '../devices/deviceModel';

/** µs epoch → Date, clamped to "now" lower-bounded sanity (bad query params → today). */
export function dateFromUsSafe(us: number): Date {
  const d = new Date(us / 1000);
  return Number.isFinite(d.getTime()) && d.getFullYear() > 2000 ? d : new Date();
}

const MAX_EVENT_PAGES = 30;

/** Drain all event pages in [fromUs, toUs) — used for clip event markers (spec 17 §4.3). */
export async function fetchAllEventsInWindow(deviceId: string, fromUs: number, toUs: number): Promise<EventItem[]> {
  const all: EventItem[] = [];
  let cursor: string | null = null;
  for (let page = 0; page < MAX_EVENT_PAGES; page++) {
    const res = await fetchDeviceEvents(deviceId, { from: fromUs, to: toUs, cursor, limit: 100 });
    all.push(...res.items);
    if (!res.nextCursor || res.items.length === 0) break;
    cursor = res.nextCursor;
  }
  return all;
}

// ---- recordings loading: lazy, cache-first, scroll/scrubber-triggered pagination ----
// spec 17 §2.2「清單載入模型」+ §2.3. A local-day's recordings are loaded ONE server page
// at a time (newest first), never eagerly for the whole day. Pages accumulate in a session
// memory cache keyed by device+day so back-scroll / day re-entry never refetch (the L2
// SQLCipher `cache_recordings` table is spec 10, still Planned → memory-only for now).
//
// Server reality (verified live, spec 17 §2.1): `/recordings` returns clips DESCENDING by
// `clip_start_ts`, and does NOT yet emit `next_cursor`. So the live pager walks `to` down
// past the oldest clip received (shrinking-window). The envelope cursor is honoured the
// moment the backend starts emitting it (D58) — no client change needed then.

/** A recording clip as consumed by both playback pages (spec 17 §2.1 response shape). */
export interface DayRecordingClip {
  clip_id: string;
  clip_start_ts: number;
  clip_end_ts: number;
  has_event: boolean;
  quality?: string;
  url: string;
}

/** Accumulated state for a device+day, returned to callers after each (lazy) load. */
export interface RecordingsResult {
  /** All clips loaded for the day so far — ascending by `clip_start_ts`, deduped by `clip_id`. */
  clips: DayRecordingClip[];
  /** No more (older) pages remain for this day → stop triggering `loadMoreRecordings`. */
  reachedStart: boolean;
}

const REC_PAGE_LIMIT = 100; // server cap (D58: default 50 / max 100, coerced — never 400)

interface CachedDay {
  clips: DayRecordingClip[]; // ascending, deduped
  nextToUs: number; // `to` boundary for the next older page (shrinking-window transition)
  nextCursor: string | null; // D58 opaque cursor once the server emits one; null → shrinking-window
  reachedStart: boolean;
  started: boolean; // loadFirst has run for this key (distinguishes "cached empty" from "unseen")
  inFlight: Promise<RecordingsResult> | null; // dedupes concurrent loadFirst/loadMore (rapid scroll)
}

const dayCache = new Map<string, CachedDay>();
const recKey = (deviceId: string, win: { fromUs: number }) => `${deviceId}|${win.fromUs}`;

/** TEST ONLY: drop the session recordings cache so each case starts cold. */
export function clearRecordingsCache(): void {
  dayCache.clear();
}

function mergeClips(existing: DayRecordingClip[], incoming: DayRecordingClip[]): DayRecordingClip[] {
  const byId = new Map<string, DayRecordingClip>();
  for (const c of existing) byId.set(c.clip_id, c);
  for (const c of incoming) byId.set(c.clip_id, c);
  return [...byId.values()].sort((a, b) => a.clip_start_ts - b.clip_start_ts);
}

async function fetchRecordingsPage(
  deviceId: string,
  fromUs: number,
  toUs: number,
  cursor: string | null,
): Promise<{ items: DayRecordingClip[]; nextCursor: string | null }> {
  const res = await apiFetch<DayRecordingClip[]>(`/v3/devices/${encodeURIComponent(deviceId)}/recordings`, {
    // 雲端 clip 皆事件錨點,has_event 篩選為 no-op 且後端回 422(spec 17 §2.1／AC-PB-2);一律不送。
    // cursor 未落地前 server 忽略此參數,帶著無害;D58 落地後同一路徑即走 cursor。
    query: cursor
      ? { from: fromUs, to: toUs, limit: REC_PAGE_LIMIT, cursor }
      : { from: fromUs, to: toUs, limit: REC_PAGE_LIMIT },
  });
  return { items: res.data ?? [], nextCursor: res.nextCursor ?? null };
}

/** Fold one fetched page into the cache entry, advancing the boundary + reachedStart flag. */
function applyPage(
  entry: CachedDay,
  win: { fromUs: number },
  page: { items: DayRecordingClip[]; nextCursor: string | null },
): void {
  entry.clips = mergeClips(entry.clips, page.items);
  if (page.nextCursor) {
    // server-driven pagination: authoritative stop is an empty/absent cursor.
    entry.nextCursor = page.nextCursor;
    return;
  }
  entry.nextCursor = null;
  // shrinking-window transition (cursor not landed): a short page = reached the day's start.
  if (page.items.length < REC_PAGE_LIMIT) {
    entry.reachedStart = true;
    return;
  }
  const minStart = Math.min(...page.items.map((c) => c.clip_start_ts));
  if (minStart >= entry.nextToUs || minStart <= win.fromUs) {
    entry.reachedStart = true; // no progress, or walked back to the day start
  }
  entry.nextToUs = minStart;
}

function ensureEntry(deviceId: string, win: { fromUs: number; toUs: number }): CachedDay {
  const key = recKey(deviceId, win);
  let entry = dayCache.get(key);
  if (!entry) {
    entry = { clips: [], nextToUs: win.toUs, nextCursor: null, reachedStart: false, started: false, inFlight: null };
    dayCache.set(key, entry);
  }
  return entry;
}

/** Synchronous cache peek — the already-loaded state for a day, or undefined if never loaded. */
export function peekRecordings(deviceId: string, win: { fromUs: number; toUs: number }): RecordingsResult | undefined {
  const e = dayCache.get(recKey(deviceId, win));
  return e && e.started ? { clips: e.clips, reachedStart: e.reachedStart } : undefined;
}

/**
 * Load the FIRST (newest) range for the day — spec 17 §2.2 lazy model. Cache-first: served
 * from the session cache with no network if the day was already opened this session.
 * Rejects on the first hard failure (ApiError 403/422) so callers can gate per camera.
 */
export async function loadFirstRecordings(
  deviceId: string,
  win: { fromUs: number; toUs: number },
): Promise<RecordingsResult> {
  const e = ensureEntry(deviceId, win);
  if (e.started) {
    // Already have the newest range cached. If a loadMore happens to be in flight, let it settle
    // but swallow its failure — a rejected OLDER-page fetch must not fail this (successful) first load.
    if (e.inFlight) await e.inFlight.catch(() => undefined);
    return { clips: e.clips, reachedStart: e.reachedStart };
  }
  if (e.inFlight) return e.inFlight;
  const p = (async () => {
    const page = await fetchRecordingsPage(deviceId, win.fromUs, win.toUs, null);
    applyPage(e, win, page);
    e.started = true;
    return { clips: e.clips, reachedStart: e.reachedStart };
  })();
  e.inFlight = p;
  try {
    return await p;
  } finally {
    e.inFlight = null;
  }
}

/**
 * Load the next OLDER range for the day (scroll-bottom / scrubber-boundary trigger).
 * No-op returning the current state when already at the day start; dedupes if a fetch is
 * already in flight. Falls back to `loadFirstRecordings` if the day was never opened.
 */
export async function loadMoreRecordings(
  deviceId: string,
  win: { fromUs: number; toUs: number },
): Promise<RecordingsResult> {
  const e = dayCache.get(recKey(deviceId, win));
  if (!e || !e.started) return loadFirstRecordings(deviceId, win);
  if (e.inFlight) return e.inFlight;
  if (e.reachedStart) return { clips: e.clips, reachedStart: true };
  const p = (async () => {
    const page = await fetchRecordingsPage(deviceId, win.fromUs, e.nextToUs, e.nextCursor);
    applyPage(e, win, page);
    return { clips: e.clips, reachedStart: e.reachedStart };
  })();
  e.inFlight = p;
  try {
    return await p;
  } finally {
    e.inFlight = null;
  }
}
